/**
 * ComparisonSetValidationPanel — the structural moment, at S3.
 *
 * PRD: docs/9-exploration/RELATIONSHIP-DETECTION-ASSIST-PRD.md §4 (phase R2).
 * Gate model: src/lib/structuralValidationCore.ts.
 *
 * THE PROBLEM THIS SOLVES. Three detectors already run against every pool —
 * Twins on identity, Nexus on structure, `poolViolations` on comparability —
 * and all three render in panels a session can walk straight past on its way
 * to scoring. "Are these actually N legitimate alternatives?" was a question
 * the product could answer and never asked. This panel asks it, once, at the
 * moment it matters (before criteria are finalized), and records the answer.
 *
 * WHAT IT IS NOT. Not a new detector, not a second route, not a lock. It is
 * inserted into the existing S3 step beside DriverAlignmentPanel (§2.6: the
 * step is inserted, never forked), it consumes the existing proposal queues,
 * and its accept/dismiss buttons call the same authority RPCs the standalone
 * review screens call. Nothing here confirms, merges or restructures anything
 * on its own — canon 12 §14.3.
 *
 * WHY IT DOES NOT HARD-BLOCK S3. The criteria step carries its own workflow
 * governance locks, and creating a second gate around them would be exactly
 * the "second path around a lock" §2.6 exists to prevent. So readiness is
 * reported and recorded, loudly, and the room decides — with an override that
 * demands a reason and keeps it forever. Governance, not bureaucracy.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Check, CircleSlash, Clock, Loader2, ShieldCheck } from 'lucide-react';
import { useSessionEngineStore } from '../store';
import { useComparabilityFindings } from '../useComparabilityBlocks';
import { useComparisonSetValidationEnabled } from '../../../hooks/useComparisonSetValidationEnabled';
import { useTenantStore } from '../../../stores/prioritization/tenantStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { acceptCluster, dismissCluster } from '../../../lib/twinsAgentService';
import { acceptNexusProposal, dismissNexusProposal } from '../../../lib/nexusAgentService';
import {
  buildStructuralValidation,
  GATE_META,
  type Finding,
  type GateId,
  type Resolution,
  type ResolutionKind,
  type StructuralValidationReport,
} from '../../../lib/structuralValidationCore';
import {
  fetchDetectorOutputForPool,
  fetchLatestStructuralValidation,
  recordStructuralValidation,
  type DetectorOutput,
  type RecordedStructuralValidation,
} from '../../../lib/structuralValidationService';
import {
  panelInset,
  LABEL_MONO,
  btnPrimary,
  btnSecondary,
  status,
  type Tone,
} from '../../../components/mono/primitives';

const EMPTY_DETECTORS: DetectorOutput = {
  clusters: [],
  proposals: [],
  error: null,
  twinsEnabled: false,
  nexusEnabled: false,
};

const READINESS_TONE: Record<StructuralValidationReport['readiness'], Tone> = {
  clear: 'green',
  nothing_found: 'green',
  overridden: 'mustard',
  warnings_outstanding: 'mustard',
  blocked: 'coral',
  // Not green. An unrun detector is an open question about the set, and the
  // one thing this panel must never do is look reassuring on evidence it does
  // not have.
  unchecked: 'mustard',
};

const READINESS_LABEL: Record<StructuralValidationReport['readiness'], string> = {
  clear: 'validated',
  nothing_found: 'nothing found',
  overridden: 'validated with override',
  warnings_outstanding: 'open findings',
  blocked: 'blocked',
  unchecked: 'not fully checked',
};

export function ComparisonSetValidationPanel() {
  const enabled = useComparisonSetValidationEnabled();
  const event = useSessionEngineStore((s) => s.event);
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const userId = useUserStore((s) => s.userId);
  const userName = useUserStore((s) => s.userName);

  // The anti-pattern half comes from the hook the enforcement path already
  // uses, so the two cannot disagree about the same pool.
  const violations = useComparabilityFindings(event);

  // Same narrowing as the comparability hook: a typed-in option has no
  // identity in the platform, so no detector has anything to say about it.
  const poolItemIds = useMemo(
    () =>
      (event?.items ?? [])
        .filter((i) => i.source === 'platform' && !i.id.startsWith('item_'))
        .map((i) => i.id),
    [event?.items],
  );
  const poolKey = poolItemIds.join(',');

  const [detectors, setDetectors] = useState<DetectorOutput>(EMPTY_DETECTORS);
  const [loading, setLoading] = useState(false);
  const [resolutions, setResolutions] = useState<Record<string, Resolution>>({});
  const [overriding, setOverriding] = useState<string | null>(null);
  const [overrideNote, setOverrideNote] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [recorded, setRecorded] = useState<RecordedStructuralValidation | null>(null);
  const [recording, setRecording] = useState(false);

  const sessionId = event?.id;

  const load = useCallback(async () => {
    if (!workspaceId || poolItemIds.length === 0) {
      setDetectors(EMPTY_DETECTORS);
      return;
    }
    setLoading(true);
    const out = await fetchDetectorOutputForPool(workspaceId, poolItemIds);
    setDetectors(out);
    setLoading(false);
  }, [workspaceId, poolItemIds]);

  useEffect(() => {
    if (!enabled) return;
    void load();
    // poolKey, not the array identity — a re-render must not refetch.
  }, [enabled, poolKey, load]);

  useEffect(() => {
    if (!enabled || !sessionId) return;
    void fetchLatestStructuralValidation(sessionId).then(({ data }) => setRecorded(data));
  }, [enabled, sessionId]);

  const report = useMemo(
    () =>
      buildStructuralValidation({
        poolItemIds,
        clusters: detectors.clusters,
        proposals: detectors.proposals,
        violations,
        resolutions,
        // Comparability is computed here and always runs; identity and
        // structure only ran if their agent is configured and on.
        checkedGates: {
          identity: detectors.twinsEnabled,
          structure: detectors.nexusEnabled,
        },
      }),
    [poolItemIds, detectors, violations, resolutions],
  );

  const titleOf = useCallback(
    (id: string) => event?.items.find((i) => i.id === id)?.title ?? id.slice(0, 8),
    [event?.items],
  );

  /**
   * Confirm or dismiss a detector's finding through the RPC that owns it.
   *
   * The local resolution is recorded only after the write succeeds — an
   * optimistic mark here would put "2 duplicates merged" into a governance
   * record for a merge that never happened.
   */
  async function disposeAgentFinding(f: Finding, kind: 'confirmed' | 'dismissed') {
    if (!userId) {
      setActionError('No signed-in user to attribute this to.');
      return;
    }
    setBusyId(f.id);
    setActionError(null);
    const isCluster = f.gate === 'identity';
    const { error } = isCluster
      ? kind === 'confirmed'
        ? await acceptCluster(f.id, userId)
        : await dismissCluster(f.id, userId)
      : kind === 'confirmed'
        ? await acceptNexusProposal(f.id, userId)
        : await dismissNexusProposal(f.id, userId);
    setBusyId(null);
    if (error) {
      setActionError(error);
      return;
    }
    setResolutions((prev) => ({ ...prev, [f.id]: { kind } }));
    // Re-read: accepting a cluster changes what the other gates may be asked.
    void load();
  }

  /** Anti-pattern findings have no row to accept — the room restructures the set. */
  function markResolved(f: Finding, kind: ResolutionKind, note?: string) {
    setResolutions((prev) => ({ ...prev, [f.id]: { kind, note } }));
  }

  function clearResolution(id: string) {
    setResolutions((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }

  async function record() {
    if (!sessionId || !userId) return;
    setRecording(true);
    setActionError(null);
    const { success, error } = await recordStructuralValidation(
      sessionId,
      userId,
      userName || 'Unknown',
      report,
      poolItemIds,
    );
    setRecording(false);
    if (!success) {
      setActionError(error ?? 'Could not record the validation.');
      return;
    }
    const { data } = await fetchLatestStructuralValidation(sessionId);
    setRecorded(data);
  }

  if (!enabled || !event) return null;
  // Fewer than two platform items: there is no comparison set to validate.
  if (poolItemIds.length < 2) return null;

  const tone = READINESS_TONE[report.readiness];

  return (
    <div {...panelInset('p-3 space-y-3')}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <span className={LABEL_MONO}>Comparison set · structural validation</span>
        <span {...status(tone)}>
          {loading ? 'reading detectors…' : READINESS_LABEL[report.readiness]}
        </span>
      </div>

      <p className="font-mono text-[0.72rem] text-[var(--text-ter)]">{report.recordLine}</p>

      {detectors.error && (
        <p className="font-mono text-[0.72rem] text-[var(--coral)]">
          Could not read the detectors ({detectors.error}) — findings below are incomplete.
        </p>
      )}

      {!report.identityResolved && (
        <p className="font-mono text-[0.72rem] text-[var(--coral)]">
          Identity first (canon Rule 13): findings about an item whose identity is unresolved are
          shown but cannot be acted on until the duplicate question is settled.
        </p>
      )}

      {report.gates.map((g) => {
        const meta = GATE_META[g.gate as GateId];
        /*
          A gate nobody ran is rendered as a labelled gap — never omitted, and
          never as a clean gate. Omitting it is what produced the false
          all-clear: the room saw two of three gates silently missing and read
          the panel as "we checked, it's fine".
        */
        if (!g.checked) {
          return (
            <div key={g.gate} className="space-y-1">
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className={LABEL_MONO}>
                  {meta.label} · {meta.source}
                </span>
                <span {...status('mustard', 'shrink-0')}>not checked</span>
              </div>
              <p className="font-mono text-[0.72rem] text-[var(--text-ter)]">
                The {meta.source} agent is not switched on for this workspace, so nothing examined
                these items for {meta.label.toLowerCase()}. This is an unchecked gate, not a clean
                one — turn the agent on in Settings › Agents to close it.
              </p>
            </div>
          );
        }
        if (g.findings.length === 0) return null;
        return (
          <div key={g.gate} className="space-y-1.5">
            <div className="flex flex-wrap items-baseline gap-x-2">
              <span className={LABEL_MONO}>
                {meta.label} · {meta.source}
              </span>
              <span className="font-mono text-[0.72rem] text-[var(--text-ter)]">{meta.blurb}</span>
            </div>

            <ul className="space-y-1.5">
              {g.findings.map((f) => {
                const resolved = f.resolution;
                const busy = busyId === f.id;
                const canAct = !f.deferred && !resolved;
                const isAgentRow = f.gate !== 'comparability';
                return (
                  <li key={f.id} className="space-y-1 border-l-2 border-[var(--rule)] pl-2">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span
                        {...status(
                          resolved
                            ? 'green'
                            : f.deferred
                              ? 'neutral'
                              : f.disposition === 'block'
                                ? 'coral'
                                : 'mustard',
                          'shrink-0',
                        )}
                      >
                        {resolved ? (
                          <Check size={10} className="inline" />
                        ) : f.deferred ? (
                          <Clock size={10} className="inline" />
                        ) : f.disposition === 'block' ? (
                          <AlertTriangle size={10} className="inline" />
                        ) : (
                          <CircleSlash size={10} className="inline" />
                        )}
                        <span className="ml-1">
                          {resolved ? resolved.kind : f.deferred ? 'deferred' : f.disposition}
                        </span>
                      </span>
                      <span className="min-w-0 flex-1 text-sm text-[var(--text-pri)]">
                        {f.detail}
                      </span>
                    </div>

                    <p className="font-mono text-[0.72rem] text-[var(--text-ter)]">
                      {f.itemIds.map(titleOf).join(' · ')}
                      {f.source !== 'anti_pattern' && ` — proposed by ${f.source}`}
                    </p>
                    {f.rationale && (
                      <p className="font-mono text-[0.72rem] text-[var(--text-ter)]">{f.rationale}</p>
                    )}
                    {f.deferred && (
                      <p className="font-mono text-[0.72rem] text-[var(--text-ter)]">
                        Behind unresolved identity for {f.deferredBehind.map(titleOf).join(' · ')}.
                      </p>
                    )}
                    {resolved?.note && (
                      <p className="font-mono text-[0.72rem] text-[var(--text-ter)]">
                        Override reason: {resolved.note}
                      </p>
                    )}

                    {canAct && overriding !== f.id && (
                      <div className="flex flex-wrap gap-1.5">
                        {isAgentRow ? (
                          <>
                            <button
                              {...btnSecondary('text-[0.72rem]')}
                              disabled={busy}
                              onClick={() => void disposeAgentFinding(f, 'confirmed')}
                            >
                              {busy ? <Loader2 size={11} className="animate-spin" /> : 'Confirm'}
                            </button>
                            <button
                              {...btnSecondary('text-[0.72rem]')}
                              disabled={busy}
                              onClick={() => void disposeAgentFinding(f, 'dismissed')}
                            >
                              Dismiss
                            </button>
                          </>
                        ) : (
                          <button
                            {...btnSecondary('text-[0.72rem]')}
                            onClick={() => markResolved(f, 'restructured')}
                          >
                            Restructured
                          </button>
                        )}
                        <button
                          {...btnSecondary('text-[0.72rem]')}
                          onClick={() => {
                            setOverriding(f.id);
                            setOverrideNote('');
                          }}
                        >
                          Override…
                        </button>
                      </div>
                    )}

                    {overriding === f.id && (
                      <div className="space-y-1">
                        <input
                          value={overrideNote}
                          onChange={(e) => setOverrideNote(e.target.value)}
                          placeholder="Why is this set legitimate anyway?"
                          aria-label="Override reason"
                          className="w-full border border-[var(--rule)] bg-[var(--bg-raised)] px-2 py-1 font-mono text-[0.72rem] text-[var(--text-pri)] placeholder:text-[var(--text-ter)] focus:border-slate-blue-400 focus:outline-none"
                        />
                        <div className="flex gap-1.5">
                          <button
                            {...btnPrimary('text-[0.72rem]')}
                            disabled={overrideNote.trim().length === 0}
                            onClick={() => {
                              markResolved(f, 'overridden', overrideNote.trim());
                              setOverriding(null);
                            }}
                          >
                            Record override
                          </button>
                          <button
                            {...btnSecondary('text-[0.72rem]')}
                            onClick={() => setOverriding(null)}
                          >
                            Cancel
                          </button>
                        </div>
                        <p className="font-mono text-[0.72rem] text-[var(--text-ter)]">
                          An override needs a reason — it is kept in the record and reported as an
                          override, never as a clean set.
                        </p>
                      </div>
                    )}

                    {resolved && resolved.kind === 'restructured' && (
                      <button
                        {...btnSecondary('text-[0.72rem]')}
                        onClick={() => clearResolution(f.id)}
                      >
                        Undo
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}

      {actionError && (
        <p className="font-mono text-[0.72rem] text-[var(--coral)]">{actionError}</p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <button {...btnPrimary()} disabled={recording || !sessionId} onClick={() => void record()}>
          {recording ? 'Recording…' : 'Record set validated'}
        </button>
        <button {...btnSecondary()} disabled={loading} onClick={() => void load()}>
          Re-read detectors
        </button>
      </div>

      {recorded && (
        <p className="flex flex-wrap items-baseline gap-x-1.5 font-mono text-[0.72rem] text-[var(--text-ter)]">
          <ShieldCheck size={11} className="shrink-0 text-[var(--text-ter)]" />
          Recorded by {recorded.actorName} on{' '}
          {new Date(recorded.validatedAt).toLocaleString()} — {recorded.recordLine}
        </p>
      )}
    </div>
  );
}
